import { useEffect, useRef, useState } from "react";
import { Mic, MicOff, CircleStop } from "lucide-react";
import { useTranslation } from "react-i18next";
import { GroundingFiles } from "@/components/ui/grounding-files";
import GroundingFileView from "@/components/ui/grounding-file-view";

import { Button } from "@/components/ui/button";
import StatusMessage from "@/components/ui/status-message";
import useRealTime from "@/hooks/useRealtime";
import useAudioRecorder from "@/hooks/useAudioRecorder";
import useAudioPlayer from "@/hooks/useAudioPlayer";
import { ConversationMessage, ConversationModal } from "@/components/conversation-modal";
import { GroundingFile, ToolResult } from "./types";
// import Toast from "@/components/ui/Toast";
import logo from "./assets/Matthew.svg";

function App() {
    const [isRecording, setIsRecording] = useState(false);
    const [conversation, setConversation] = useState<ConversationMessage[]>([]);
    const [groundingFiles, setGroundingFiles] = useState<GroundingFile[]>([]);
    const [selectedFile, setSelectedFile] = useState<GroundingFile | null>(null);
    const [conversationFinished, setConversationFinished] = useState(false);

    // Extract user name from URL parameters
    const [userName, setUserName] = useState<string | null>(null);

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const user = urlParams.get("user");
        setUserName(user);
    }, []);

    // At the top of App:
    const [isInturpted, setIsInturpted] = useState(true);
    const [isMicMuted, setIsMicMuted] = useState(false);
    const isMicMutedRef = useRef(isMicMuted);
    useEffect(() => {
        isMicMutedRef.current = isMicMuted;
    }, [isMicMuted]);

    const { startSession, addUserAudio, inputAudioBufferClear, closeSession } = useRealTime({
        userName: userName, // Pass userName to the hook
        onWebSocketOpen: () => console.log("WebSocket connection opened"),
        onWebSocketClose: () => console.log("WebSocket connection closed"),
        onWebSocketError: event => console.error("WebSocket error:", event),
        onReceivedError: message => console.error("error", message),

        onReceivedResponseAudioDelta: message => {
            if (isInturpted) {
                stopAudioPlayer();
                setIsMicMuted(false); // Mute mic when audio starts

                return;
            }
            setIsMicMuted(true); // Mute mic when audio starts

            if (isRecording) {
                playAudio(message.delta, () => {
                    setIsMicMuted(false);
                    setIsInturpted(false);
                });
            }
        },
        onReceivedExtensionMiddleTierToolResponse: msg => {
            const result: ToolResult = JSON.parse(msg.tool_result);
            console.log("result", result);
            const files: GroundingFile[] = result.sources.map(x => {
                return { id: x.chunk_id, name: x.title, content: x.chunk };
            });
            console.log("files", result.sources);
            setGroundingFiles(prev => [...prev, ...files]);
        },
        onWebSocketMessage: msg => console.log(msg, "onWebSocketMessage"),
        onReceivedResponseAudioTranscriptDone: msg => console.log(msg, "onReceivedResponseAudioTranscriptDone"),

        enableInputAudioTranscription: true,
        onReceivedInputAudioBufferSpeechStarted: () => {
            stopAudioPlayer();
        },
        onReceivedResponseAudioTranscriptDelta: message => {
            if (isInturpted) {
                stopAudioPlayer();
                setIsMicMuted(false); // Mute mic when audio starts

                return;
            }

            setConversation(prev => {
                const existingMessageIndex = prev.findIndex(m => m.responseId === message.response_id && !m.isComplete);

                if (existingMessageIndex !== -1) {
                    return prev.map((msg, idx) => (idx === existingMessageIndex ? { ...msg, content: msg.content + message.delta } : msg));
                }

                return [
                    ...prev,
                    {
                        role: "Assistant",
                        content: message.delta,
                        responseId: message.response_id,
                        isComplete: false,
                        timestamp: new Date().toISOString().replace("Z", "")
                    }
                ];
            });
        },
        onReceivedResponseDone: message => {
            console.log(message.response.usage, "usage is");

            setConversation(prev => prev.map(msg => (msg.responseId === message.response.id ? { ...msg, isComplete: true } : msg)));
        },
        onReceivedInputAudioTranscriptionCompleted: message => {
            setIsInturpted(false);

            setConversation(prev => [
                ...prev,
                { role: "User", content: message.transcript, isComplete: true, timestamp: new Date().toISOString().replace("Z", "") }
            ]);
        }
    });

    const { reset: resetAudioPlayer, play: playAudio, stop: stopAudioPlayer } = useAudioPlayer();
    const { start: startAudioRecording, stop: stopAudioRecording } = useAudioRecorder({
        onAudioRecorded: base64 => {
            if (!isMicMutedRef.current) {
                addUserAudio(base64);
            }
        }
    });

    const onToggleListening = async () => {
        if (!isRecording) {
            setConversation([]);

            startSession();
            await startAudioRecording();
            resetAudioPlayer();
            setIsRecording(true);
            setConversationFinished(false);
        } else {
            await stopAudioRecording();
            stopAudioPlayer();
            inputAudioBufferClear();
            setIsRecording(false);
            setIsMicMuted(false);
            setIsInturpted(true);
            closeSession();
            setConversationFinished(true);
            // After stopping the interview, send the data and show the modal
            // setShowThankYouModal(true);
        }
    };

    const { t } = useTranslation();

    // const triggerToast = () => {
    //     setMessage("Passcode validated successfully");
    // };
    return (
        <div className="flex min-h-screen flex-col">
            <header className="relative py-6">
                <img src={logo} alt="Matthew logo" className="absolute left-8 top-8 z-10 h-12 w-auto object-contain" />
                <div className="container relative mx-auto px-0">
                    <h1 className="absolute left-1/2 top-6 -translate-x-1/2 bg-gradient-to-r from-blue-600 via-cyan-500 to-blue-800 bg-clip-text text-4xl font-extrabold text-transparent">
                        {t("app.title")}
                    </h1>
                </div>
            </header>
            {/* <Toast message={message} /> */}

            <div className="flex flex-grow items-center justify-center">
                <section className="msger">
                    <>
                        {(isRecording || conversation.length > 0) && (
                            <main className={` ${conversation.length > 0 && "msger-chat vh-10"}`} style={{ borderRadius: "15px" }}>
                                <ConversationModal messages={conversation} />
                            </main>
                        )}
                        <main className="p-4" style={{ backgroundColor: "rgb(241, 249, 255)", borderRadius: "15px" }}>
                            <div className={`mb-4 flex items-center justify-center ${conversation.length == 0 && "pt-10"} `}>
                                {!conversationFinished ? (
                                    <>
                                        <Button
                                            onClick={onToggleListening}
                                            className={`btn-bg mb-4 ${isRecording ? "bg-red-500 hover:bg-red-600" : "bg-green-500 hover:bg-green-600"}`}
                                            aria-label={isRecording ? t("app.stopRecording") : t("app.startRecording")}
                                        >
                                            {isRecording ? (
                                                <>
                                                    <MicOff className="mr-2 h-5 w-5" />
                                                    {t("Finish conversation")}
                                                </>
                                            ) : (
                                                <>
                                                    <Mic className="mr-2 h-6 w-6" />
                                                    {t("Start conversation")}
                                                </>
                                            )}
                                        </Button>
                                        {isMicMuted && (
                                            <Button
                                                onClick={() => {
                                                    stopAudioPlayer();
                                                    setIsMicMuted(false);
                                                    setIsInturpted(true);
                                                }}
                                                className="mb-4 ml-5 bg-yellow-500 hover:bg-yellow-600"
                                                aria-label="Interrupt audio"
                                            >
                                                <CircleStop className="mr-2 h-6 w-6" />
                                                Interrupt
                                            </Button>
                                        )}
                                    </>
                                ) : (
                                    <Button
                                        onClick={() => window.location.reload()}
                                        className="btn-bg mb-4 bg-blue-500 hover:bg-blue-600"
                                        aria-label="Refresh page"
                                    >
                                        {t("Refresh")}
                                    </Button>
                                )}
                            </div>

                            <StatusMessage isRecording={isRecording} />
                        </main>
                    </>
                </section>
                <GroundingFiles files={groundingFiles} onSelected={setSelectedFile} />
            </div>
            <footer
                className="absolute bottom-6 left-1/2 -translate-x-1/2 bg-clip-text text-base font-extrabold text-transparent"
                style={{ backgroundImage: "linear-gradient(to right, rgb(107 114 128), rgb(55 65 81), rgb(0 0 0))" }}
            >
                <p>{t("app.footer")}</p>
            </footer>
            <GroundingFileView groundingFile={selectedFile} onClosed={() => setSelectedFile(null)} />
        </div>
    );
}

export default App;
